<script setup>
  import { ref, onBeforeMount, computed } from "vue";
  import { useCompanyStore } from "@/stores/company";
  import { useUiStore } from "@/stores/ui";
  import { useDataCollectionStore } from "@/stores/dataCollection";

  const uiStore = useUiStore();
  const dataCollectionStore = useDataCollectionStore();
  const getModalInfo = computed(()=> uiStore.modalData);
  const getModalAction = computed(()=> uiStore.modalAction);
  const companyId = useCompanyStore().companyId;

  let mappedFiles = ref([]);
  let mappedUrls = ref([]);
  let mappedIntegrations = ref([]);
  const isLoading = ref(true);
  const activeTab = ref('files');
  const resetStoreData = () => {
    uiStore.resetModalStore();
  };

  const switchTab = (tab) => {
    activeTab.value = tab;
  };

  const action = async (actionName, data) => {
    //    await resetStoreData();

    uiStore.setModalAction(actionName);
    uiStore.setModalState(true);
    uiStore.setModalData(data);
  };

  onBeforeMount(()=>{

    if(getModalInfo.value && getModalAction.value == 'mapped_files'){

      dataCollectionStore.fetchDataSourceById({companyId: companyId, id: getModalInfo.value.id}).then((res) => {
        mappedFiles.value = res.data.files;
        mappedUrls.value = res.data.urls;
        mappedIntegrations.value = res.data.integrations;
        isLoading.value = false;
      }).catch(() => {})
    }

  });

</script>
<template>
  <DModal v-if="getModalAction === 'mapped_files'" :visible="uiStore.modalState" @update:visible="val => { if (!val) resetStoreData() }" :main_classes="[]" :dialog_classes="[]">
    <template #title> Mapped Data </template>
    
    <template #body>
        
      <div v-if="isLoading" class="flex justify-center mb-3">
        <div class="spinner-border text-success" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else>
        <!-- Tabs Navigation -->
        <div class="tabs-navigation">
          <button 
            @click="switchTab('files')" 
            class="tab-button" 
            :class="{ active: activeTab === 'files' }">
            Files
          </button>
          <button 
            @click="switchTab('urls')" 
            class="tab-button" 
            :class="{ active: activeTab === 'urls' }">
            URLs
          </button>
          <button 
            @click="switchTab('integrations')" 
            class="tab-button" 
            :class="{ active: activeTab === 'integrations' }">
            Integrations
          </button>
        </div>

        <!-- Files Tab Content -->
        <div v-show="activeTab === 'files'" class="tab-content">
          <div class="files-container">
            <div v-if="mappedFiles && mappedFiles.length" class="card-body files-grid pl-0">
              <div v-for="(file, index) in mappedFiles" :key="index" class="files-card">
                <div class="files-content">
                  <h3>{{ file.name }}</h3>
                  <p>{{ file.file_type }}</p>
                </div>
                <div class="files-actions">
                   <button @click="action('delete_folder_and_job', file)"  type="button" class="icon-button danger" title="Delete"><i class="dx-icon-trash"></i></button>
                </div>
              </div>
            </div>
    
            <div v-else class="no-data-available">
              <p>No files uploaded.</p>
            </div>
          </div>
        </div>

        <!-- URLs Tab Content -->
        <div v-show="activeTab === 'urls'" class="tab-content">
          <div class="files-container">
            <div v-if="mappedUrls && mappedUrls.length" class="card-body files-grid pl-0">
              <div v-for="(url, index) in mappedUrls" :key="index" class="files-card">
                <div class="files-content">
                  <h3>{{ url.url }}</h3>
                  <p>{{ url.status || 'URL' }}</p>
                </div>
                <div class="files-actions">
                   <button @click="action('delete_data_collection_url', url)"  type="button" class="icon-button danger" title="Delete"><i class="dx-icon-trash"></i></button>
                </div>
              </div>
            </div>
    
            <div v-else class="no-data-available">
              <p>No URLs added.</p>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'integrations'" class="tab-content">
          <div class="files-container">
            <div v-if="mappedIntegrations && mappedIntegrations.length" class="card-body files-grid pl-0">
              <div v-for="(integration, index) in mappedIntegrations" :key="index" class="files-card">
                <div class="files-content">
                  <h3>{{ integration.name }}</h3>
                  <p>{{ integration.status || 'Integration' }}</p>
                </div>
                <!-- <div class="files-actions">
                   <button @click="action('delete_data_collection_integration', integration)"  type="button" class="icon-button danger" title="Delete"><i class="dx-icon-trash"></i></button>
                </div> -->
              </div>
            </div>
    
            <div v-else class="no-data-available">
              <p>No Integrations added.</p>
            </div>
          </div>
        </div>
      </div>

    </template>

    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex mt-20 justify-center">
          <!-- <button :disabled="!name || isSubmitted" @click="onSubmit" type="button" class="btn btn-primary add-button mr-30">Edit 
            <span v-if="isSubmitted" class="spinner-sm" role="status" aria-hidden="true"></span></button> -->
          <button @click="resetStoreData" type="button" class="btn btn-secondary cancel-button"> Cancel </button>
        </div>
      </div>
    </template>

  </DModal>
</template>
<style scoped lang="scss">

.tabs-navigation {
  display: flex;
  gap: var(--spacing-2);
  border-bottom: 2px solid var(--neutral-200);
  margin-bottom: var(--spacing-4);
  
  .tab-button {
    padding: var(--spacing-3) var(--spacing-4);
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--neutral-600);
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: -2px;
    
    &:hover {
      color: var(--primary-600);
      background-color: var(--primary-50);
    }
    
    &.active {
      color: var(--primary-600);
      border-bottom-color: var(--primary-600);
      font-weight: 600;
      border-radius: 0px;
    }
  }
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.files-container {
  width: 100%;
}

.card-body {
  padding: 0;
  width: 100%;
}

.files-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: var(--spacing-3);
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .files-card {
    padding: var(--spacing-4);
    border: 1px solid var(--neutral-200);
    border-radius: var(--border-radius-md);
    display: flex;
    gap: var(--spacing-3);
    width: 100%;
    align-items: center;
    
    &:hover {
      border-color: var(--primary-200);
      background-color: var(--primary-50);
      
      .files-icon {
        background-color: var(--primary-500);
        color: white;
      }
    }
  }

  .files-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--border-radius-md);
    background-color: var(--neutral-100);
    color: var(--neutral-600);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
  }

  .files-content {
    flex: 1;
    min-width: 0;
    
    h3 {
      margin: 0 0 var(--spacing-1) 0;
      font-size: var(--text-base);
      word-break: break-word;
    }
    
    p {
      margin: 0;
      font-size: var(--text-sm);
      color: var(--neutral-600);
    }
  }

  .files-actions{
    margin-left: auto;
    gap: var(--spacing-2);
    flex-shrink: 0;
  }

}
</style>
